import React, { useEffect, useState } from "react";
import { Bar, Chart, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import {
  Chart as ChartJS,
  LineElement,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale,
} from "chart.js";
import { formatNumberToShortCurrency } from "../../helpers/utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  ChartDataLabels,
  Title,
  Tooltip,
  Legend,
  MatrixController,
  MatrixElement,
  LogarithmicScale
);

export const RiskMap = ({
  riskData = [],
  onCellClick,
  highlightedRiskMapCell,
}) => {
  const getColor = (severity) => {
    if (severity >= 17) return "#C50F1F";
    if (severity >= 9) return "#EAA300";
    if (severity >= 0) return "#0B6A0B";
    return "#E5E5E5";
  };

  const generateCompleteGrid = () => {
    const completeData = [];
    for (let impact = 1; impact <= 5; impact++) {
      for (let probability = 1; probability <= 5; probability++) {
        completeData.push({
          x: impact,
          y: probability,
          v: riskData.filter(
            (r) => r.impact == impact && r.probability == probability
          ).length,
        });
      }
    }
    return completeData;
  };

  const data = {
    datasets: [
      {
        label: "Risk Matrix",
        data: generateCompleteGrid(),
        backgroundColor(context) {
          const { x, y } = context.raw;
          const baseColor = getColor(x * y);
  
          if (highlightedRiskMapCell) {
            if (highlightedRiskMapCell.x === x && highlightedRiskMapCell.y === y) {
              return baseColor;
            }
            return baseColor + "50";
          }
  
          return baseColor;
        },
        borderRadius: 5,
        width: ({ chart }) => (chart.chartArea?.width || 300) / 5 - 4,
        height: ({ chart }) => (chart.chartArea?.height || 300) / 5 + 2,
      },
    ],
  };

  const options = {
    layout: {
      padding: {
        top: 30,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const item = items[0];
            return `Impact: ${item.raw.x}, Probability: ${item.raw.y}`;
          },
          label: (item) => {
            return `Severity: ${item.raw.x * item.raw.y}`;
          },
        },
      },
      datalabels: {
        color: (context) => {
          const value = context.dataset.data[context.dataIndex].v;
          return value === 0 ? "#FFFFFF" : "#FFFFFF";
        },
        font: { weight: "bold", size: 14 },
        align: "center",
        formatter(value) {
          return value.v > 0 ? value.v : "-";
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 1,
        max: 5,
        ticks: {
          stepSize: 1,
          callback: (value) => {
            return value;
          },
          clip: false,
          padding: 20,
        },
        title: {
          display: true,
          text: "Impact",
          font: {
            size: 14,
            weight: "bold",
            family: "Figtree-SemiBold",
          },
        },
        grid: { display: false },
        border: {
          display: false,
        },
      },
      y: {
        type: "linear",
        min: 1,
        max: 5,
        reverse: false,
        ticks: { stepSize: 1 },
        title: {
          display: true,
          text: "Probability",
          font: {
            size: 14,
            weight: "bold",
            family: "Figtree-SemiBold",
          },
        },
        grid: { display: false },
        border: {
          display: false,
        },
      },
    },
    onClick: (event, elements) => {
      if (elements && elements.length > 0) {
        const dataIndex = elements[0].index;
        const dataPoint = data.datasets[0].data[dataIndex];
        const severity = dataPoint.v;
        if (onCellClick) {
          onCellClick(severity, dataPoint);
        }
      }
    },
    onHover: (event, chartElement) => {
      if (chartElement.length) {
        event.native.target.style.cursor = "pointer";
      } else {
        event.native.target.style.cursor = "default";
      }
    },
  };

  return <Chart type="matrix" data={data} options={options} height={200} />;
};

export const RiskMapSkeletonLoader = () => {
  // Generate grid cells for the skeleton
  const generateSkeletonGrid = () => {
    const cells = [];
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 5; col++) {
        cells.push(
          <div
            key={`${row}-${col}`}
            className="rounded bg-gray-200 animate-pulse"
            style={{
              width: `calc(20% - 4px)`,
              height: `calc(20% - 4px)`,
              margin: "2px",
            }}
          />
        );
      }
    }
    return cells;
  };

  return (
    <div className="relative w-full" style={{ height: "200px" }}>
      <div className="absolute inset-0 flex flex-col pt-8">
        <div className="flex-1 flex flex-wrap justify-center items-center">
          {generateSkeletonGrid()}
        </div>
      </div>

      <div className="absolute bottom-2 left-0 right-0 flex justify-center">
        <div className="w-16 h-4 bg-gray-200 rounded animate-pulse" />
      </div>

      <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
        <div className="h-16 w-4 bg-gray-200 rounded animate-pulse" />
      </div>

      <div className="absolute bottom-8 w-full flex justify-between px-8">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"
          />
        ))}
      </div>

      <div className="absolute left-8 h-full flex flex-col justify-between py-8">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"
          />
        ))}
      </div>
    </div>
  );
};

export const ProjectProgress = (props) => {
  const { progress, className, isLoading = false } = props;

  const percentage = progress?.progress ?? 0;
  const completedItems = progress?.progressCount ?? 0;
  const totalItems = progress?.totalCount ?? 0;
  const status = progress?.status ?? 0;

  const data = {
    datasets: [
      {
        data: [percentage, 100 - percentage],
        backgroundColor: [getGlobalColor(status), "#EFEFEF"],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  const options = {
    cutout: "80%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
  };

  return (
    <div className="flex flex-col items-center justify-center w-36">
      <div className="relative w-36 h-24">
        <Doughnut data={data} options={options} />

        <div className="absolute inset-0 flex flex-col items-center justify-center mt-5">
          <div className="progress-percentage px-3">{percentage}%</div>
          <div className="progress-subtext px-3">
            {completedItems}/{totalItems} in progress
          </div>
        </div>
      </div>
    </div>
  );
};

export const BudgetChart = (props) => {
  const { budgetSummary, currencySymbol, className, isLoading = false } = props;
  let summary = budgetSummary ??
  {
    budget: 0,
    spent: 0,
    etc: 0,
    bac: 0,
  };
  const labels = ["Budget", "Spent", "ETC", "BAC"];
  const maxValue = Math.max(summary.budget, summary.spent, summary.etc, summary.bac);
  const yMax = maxValue * 1.2;
  const isOverBudget = Number(summary.budget) < Number(summary.bac);
  const data = {
    labels,
    datasets: [
      {
        data: [summary.budget, summary.spent, summary.etc, summary.bac],
        backgroundColor: ["#4C41B1", "#4F9C9E", "#A1B3D1", isOverBudget ? '#C50F1F' : '#107c10'],
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {      
          color: '#292929',
          font: {
            size: 12,
            family: 'Figtree-SemiBold'
          },
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        max: yMax,
      },
    },
    barPercentage: 0,
    categoryPercentage: 0,
  };

  return (
    <div className={`budget-chart ${className}`}>
      <div className="flex justify-between pb-1 px-2">
        <div className="text-budget">
          {formatNumberToShortCurrency(summary.budget, currencySymbol, 2)}
        </div>
        <div className="text-budget">
          {formatNumberToShortCurrency(summary.spent, currencySymbol, 2)}
        </div>
        <div className="text-budget">
          {formatNumberToShortCurrency(summary.etc, currencySymbol, 2)}
        </div>
        <div className={`text-budget ${isOverBudget ? "text-bac-red" : "text-bac-green"}`}>
          {formatNumberToShortCurrency(summary.bac, currencySymbol, 2)}
        </div>
      </div>
      <Bar options={options} data={data} />
    </div>
  );
};

export const IssueChart = ({ issues }) => {
  const lowRisk = issues.find((issue) => issue.status === 0)?.count;
  const moderateRisk = issues.find((issue) => issue.status === 1)?.count;
  const highRisk = issues.find((issue) => issue.status === 2)?.count;

  const hasData = lowRisk > 0 || moderateRisk > 0 || highRisk > 0;

  const data = {
    labels: ["Low", "Moderate", "High"],
    datasets: [
      {
        data: [lowRisk, moderateRisk, highRisk],
        backgroundColor: [
          getGlobalColor(0),
          getGlobalColor(1),
          getGlobalColor(2),
        ],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "60%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: (context) => {
          return context.dataset.data[context.dataIndex] > 0;
        },
        color: "white",
        font: {
          size: 10,
          weight: "bold",
        },
      },
    },
  };

  return (
    <div className={`w-32 h-24 flex flex-col items-center`}>
      <div className="w-28 h-24">
        <Doughnut options={options} data={data} />
      </div>
      {
        hasData && (
<div className="flex flex-row mt-2 text-xs justify-center w-full">
        <div className="flex items-center mr-2 ">
          <div
            className="w-2 h-2 mr-1"
            style={{ backgroundColor: getGlobalColor(0) }}
          ></div>
          <span>Low</span>
        </div>
        <div className="flex items-center mr-2">
          <div
            className="w-2 h-2 bg-yellow-500 mr-1"
            style={{ backgroundColor: getGlobalColor(1) }}
          ></div>
          <span>Moderate</span>
        </div>
        <div className="flex items-center">
          <div
            className="w-2 h-2 mr-1"
            style={{ backgroundColor: getGlobalColor(2) }}
          ></div>
          <span>High</span>
        </div>
      </div>
        )
      }
      
    </div>
  );
};

export const getGlobalColor = (colorType) => {
  switch (colorType) {
    case 0:
    case "0":
      return "#0B6A0B";
    case 1:
    case "1":
      return "#EAA300";
    case 2:
    case "2":
      return "#C50F1F";

    default:
      return "#EFEFEF";
  }
};
